// @ts-nocheck
/* Verbatim port of v1 CountryCodeDirectionPicker.js */
import React, { useMemo } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const _Unused_Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const _Unused_Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
_Unused_Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
_Unused_Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
_Unused_Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
_Unused_Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('card-text', className)} {...props}>
    {children}
  </p>
);
_Unused_Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import Select, { components } from 'react-select';
import { FiGlobe } from 'react-icons/fi';
import { SelectAllToggle } from '@/components/ui/select-all-toggle';

export function DirectionIcon({ size = 16 }) {
  return <FiGlobe size={size} color="#64748b" />;
}

function buildOption(item) {
  const country = item.country_code || {};
  const callingCode = country.calling_code ? `+${country.calling_code}` : '';
  return {
    value: item.id,
    countryName: country.name,
    callingCode,
    isEnabled: item.is_enabled !== false,
    label: `${country.name || 'Unknown'} ${callingCode}`,
  };
}

const OptionRow = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <DirectionIcon />
      <span>{props.data.label}</span>
      {!props.data.isEnabled && (
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>Disabled</span>
      )}
    </div>
  </components.Option>
);

const MultiValueLabelRow = (props) => (
  <components.MultiValueLabel {...props}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <DirectionIcon />
      {props.data.label}
    </span>
  </components.MultiValueLabel>
);

const CountryCodeDirectionPicker = ({
  items = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select Country Codes',
  isDisabled = false,
  label = 'Country Codes:',
  required = false,
  groupClassName = 'col-md-6 form-group',
  iconWrap = false,
  showSelectAll = false,
}) => {
  const options = useMemo(
    () => items.filter((i) => i.is_enabled !== false).map(buildOption),
    [items],
  );

  const selectedIdSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds],
  );

  const value = useMemo(
    () => options.filter((opt) => selectedIdSet.has(String(opt.value))),
    [options, selectedIdSet],
  );

  const selectEl = (
    <Select
      className={`theme-react-select${iconWrap ? ' ccf-select-in-icon' : ''}`}
      classNamePrefix="theme-react-select"
      isMulti
      isDisabled={isDisabled}
      options={options}
      value={value}
      onChange={(selected) => onChange((selected || []).map((opt) => opt.value))}
      placeholder={placeholder}
      components={{
        Option: OptionRow,
        MultiValueLabel: MultiValueLabelRow,
      }}
    />
  );

  return (
    <Form.Group className={groupClassName}>
      <Form.Label className={showSelectAll ? 'd-flex align-items-center w-100' : undefined}>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
        {showSelectAll && (
          <SelectAllToggle
            allIds={options.map((o) => o.value)}
            selectedIds={selectedIds}
            onChange={onChange}
            disabled={isDisabled}
          />
        )}
      </Form.Label>
      {iconWrap ? (
        <div className="ccf-input-with-icon">
          <span className="ccf-input-with-icon__icon">
            <FiGlobe size={18} />
          </span>
          {selectEl}
        </div>
      ) : (
        selectEl
      )}
    </Form.Group>
  );
};

export default CountryCodeDirectionPicker;
